import React, { Fragment } from 'react';
import { FormattedDate, FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import AutopayCancelModal from 'components/Core/Autopay-schedule/CancelModal/AutopayCancelModal';
import ForcedAutopayScheduledContent from 'components/Core/Autopay-schedule/ForcedAutopay/ForcedAutopayScheduledContent';
import RegularScheduledContent from 'components/Core/Autopay-schedule/PaymentScheduled/RegularScheduledContent';
import SchedulePayCancelModal from 'components/Core/Autopay-schedule/SchedulePayCancelModal/SchedulePayCancelModal';
import MainFooter from 'components/Core/Footer/MainFooterComponent/MainFooter';
import Hr from 'components/Shared/Hr/Hr';
import { GreenSuccessIcon } from 'components/Shared/Icons/Icons';
import Separator from 'components/Shared/Separator/Separator';
import SegmentIO from 'reporting/SegmentIO';
import { toDate } from 'shared/utils/dateUtils';
import { signIn } from 'store/auth/slice';
import { modalActions } from 'store/modal/slice';
import { cancelScheduledPayment } from 'store/payment/slice';
import { isForcedAutopayRecurringSelector } from 'store/sale/selectors';
import { AppDispatch } from 'store/store';
import { useSelector } from 'store/utils';
import { breakpoints, colors } from 'styles/cp';
import { Modal } from 'types/Modal';

export interface Props extends WrappedComponentProps {
  balance: number;
  currency: string;
  companyName: string;
  isAutoPay: boolean;
  isSubscription: boolean;
  scheduledDate: string | number | Date;
  isUserSignedIn?: boolean;
  companyLocale: string;
  isSignedInUserTheScheduler?: boolean;
  showModal: (Component: Pick<Modal, 'component' | 'componentProps'>, args?: Object) => void;
  hideModal: () => void;
  recurringInfo: any;
  featureFlags: Record<string, any>;
  isPaymentScheduleSuccessful?: boolean;
}

const PaymentScheduled: React.FC<Props> = ({
  isAutoPay,
  isSubscription,
  showModal,
  hideModal,
  balance,
  currency,
  companyName,
  companyLocale,
  scheduledDate,
  isUserSignedIn = false,
  isSignedInUserTheScheduler = false,
  recurringInfo,
  featureFlags,
  isPaymentScheduleSuccessful = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const isForcedAutoPay = useSelector(isForcedAutopayRecurringSelector);

  const formattedScheduledDate = toDate(scheduledDate);

  const cancelByDate = new Date(formattedScheduledDate);
  cancelByDate.setDate(cancelByDate.getDate() - 1);

  const shouldShowCTA =
    isAutoPay ||
    (!isAutoPay && formattedScheduledDate.toDateString() !== new Date().toDateString());

  const cancel = () => {
    SegmentIO.transactionCancelled({
      activity_type: isAutoPay ? 'auto_pay' : 'schedule_pay',
      ui_object: 'button',
      ui_object_detail: isAutoPay ? 'cancel_autopay' : 'cancel_payment',
      ui_action: 'clicked',
      ui_access_point: 'modal',
    });
    dispatch(cancelScheduledPayment());
  };
  const signInCallback = () => {
    SegmentIO.transactionEngaged({
      ui_object: 'link',
      ui_object_detail: 'sign_in_cancel_auto_pay/exit',
      ui_action: 'clicked',
      ui_access_point: 'modal',
    });
    dispatch(signIn());
  };
  const hideCancelModal = () => {
    SegmentIO.transactionCancelled({
      activity_type: isAutoPay ? 'auto_pay' : 'schedule_pay',
      ui_object: 'button',
      ui_object_detail: isAutoPay ? 'autopay_not_canceled' : 'keep_schedule_pay',
      ui_action: 'clicked',
      ui_access_point: 'modal',
    });
    hideModal();
  };

  const openModal = () => {
    const ModalContent = () =>
      isAutoPay ? (
        <AutopayCancelModal
          hide={hideCancelModal}
          onContinue={cancel}
          nextPaymentDate={new Date(recurringInfo.nextDate).toLocaleDateString()}
          featureFlags={featureFlags}
          companyLocale={companyLocale}
        />
      ) : (
        <SchedulePayCancelModal hide={hideCancelModal} onContinue={cancel} />
      );
    showModal({
      component: ModalContent,
      componentProps: {
        closeOnClickOutSide: false,
        closeOnEsc: false,
        showCloseButton: false,
      },
    });
  };

  let autoPayInterval;
  if (recurringInfo && typeof recurringInfo.recurringDescription === 'string') {
    autoPayInterval = recurringInfo.recurringDescription.toLowerCase();
  }

  const getHeaderText = () => {
    if (isForcedAutoPay) {
      return { id: 'SUCCESS_SCREEN_PAYMENT_SCHEDULED_SUCCESS', defaultMessage: 'Success' };
    }

    return { id: 'PAYMENT_SCHEDULED', defaultMessage: 'Payment scheduled' };
  };

  const headerText = getHeaderText();

  let cancellationInfo;
  if (!isUserSignedIn || isSignedInUserTheScheduler) {
    cancellationInfo = (
      <Fragment>
        <div className="cancel-by">
          <FormattedMessage
            id="CAN_BE_CANCELED_BY"
            defaultMessage="This payment can be canceled by date "
          />
          <FormattedDate
            value={cancelByDate}
            timeZone={undefined}
            month="numeric"
            year="numeric"
            day="numeric"
          />
        </div>

        <style jsx>{`
          .cancel-by {
            font-family: AvenirNextforINTUIT-Regular;
            font-size: 12px;
          }
        `}</style>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="container">
        <div className="header-bar" />
        <Separator height={30} />
        <GreenSuccessIcon />
        <Separator height={20} />
        <div className="header-text">
          <FormattedMessage id={headerText.id} defaultMessage={headerText.defaultMessage} />
        </div>
        <Separator height={24} />

        {isForcedAutoPay ? (
          <ForcedAutopayScheduledContent
            balance={balance}
            currency={currency}
            companyName={companyName}
            isUserSignedIn={isUserSignedIn}
            isSignedInUserTheScheduler={isSignedInUserTheScheduler}
            signInCallback={signInCallback}
            openModal={openModal}
            isPaymentScheduleSuccessful={isPaymentScheduleSuccessful}
          />
        ) : (
          <RegularScheduledContent
            balance={balance}
            currency={currency}
            companyName={companyName}
            formattedScheduledDate={formattedScheduledDate}
            isAutoPay={isAutoPay}
            isSubscription={isSubscription}
            autoPayInterval={autoPayInterval}
            shouldShowCTA={shouldShowCTA}
            isSignedInUserTheScheduler={isSignedInUserTheScheduler}
            isUserSignedIn={isUserSignedIn}
            signInCallback={signInCallback}
            openModal={openModal}
            cancellationInfo={cancellationInfo}
          />
        )}

        <Fragment>
          {!isForcedAutoPay && <Hr marginBottom={15} />}
          <MainFooter
            redesign={false}
            companyLocale={companyLocale}
            isPayable={false}
            bottomPadding={15}
          />
        </Fragment>
      </div>

      <style jsx>{`
        .container {
          width: 100%;
          display: flex;
          align-items: center;
          flex-direction: column;
          background-color: ${colors.white};

          .header-bar {
            height: 4px;
            width: 100%;
            background-image: linear-gradient(262deg, ${colors.blue02}, ${colors.green02});
            text-align: center;
            border-top-left-radius: 9px;
            border-top-right-radius: 9px;
          }

          .header-text {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: 24px;
          }

          .invoice-amount {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: 26px;
          }

          .company-name {
            font-family: AvenirNextforINTUIT-Medium;
            font-size: 16px;
          }

          .scheduled-date {
            font-family: AvenirNextforINTUIT-Regular;
            font-size: 14px;

            .scheduled-for {
              @media screen and (max-width: ${breakpoints.md}) {
                display: none;
              }
            }
          }
        }
      `}</style>
    </Fragment>
  );
};

export { PaymentScheduled };

const withIntl = injectIntl(PaymentScheduled);
export default connect(
  () => {
    return {};
  },
  {
    showModal: modalActions.show,
    hideModal: modalActions.hide,
  }
)(withIntl);
